// Phase H.13b — Clause library admin page (super_admin only).
//
// List + create + edit + archive. Each clause edits in a slim MiniEditor
// reused from H.12a — same compact toolbar admins are used to from the
// header/footer panel.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { DashboardShell } from "@/components/DashboardShell";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import MiniEditor from "@/components/editor/MiniEditor";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
    BookOpen, Search, Plus, Trash2, Archive, ArchiveRestore, Save, X, Loader2,
} from "lucide-react";

const MAX_BODY_CHARS = 5000;

function _EmptyShell({ children }) {
    return (
        <DashboardShell>
            <div className="max-w-6xl mx-auto p-6">{children}</div>
        </DashboardShell>
    );
}

export default function ClausesPage() {
    const { user } = useAuth();
    const isSuperAdmin = user?.role === "super_admin";

    const [clauses, setClauses] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showArchived, setShowArchived] = useState(false);
    const [search, setSearch] = useState("");
    const [editing, setEditing] = useState(null);   // currently-edited clause OR a brand-new shell
    const [busy, setBusy] = useState(false);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get(`/admin/clauses?archived=${showArchived ? "true" : "false"}`);
            setClauses(r.data?.items || []);
            setCategories(r.data?.categories || []);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, [showArchived]);

    useEffect(() => { refresh(); }, [refresh]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return clauses;
        return clauses.filter((c) => {
            const hay = [c.name, c.description, c.category].filter(Boolean).join(" ").toLowerCase();
            return hay.includes(q);
        });
    }, [clauses, search]);

    const grouped = useMemo(() => {
        const map = new Map();
        for (const c of filtered) {
            const cat = c.category || "Uncategorized";
            if (!map.has(cat)) map.set(cat, []);
            map.get(cat).push(c);
        }
        for (const arr of map.values()) arr.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
    }, [filtered]);

    const startNew = () => {
        setEditing({
            id: null,
            name: "",
            description: "",
            category: "",
            body_html: "",
        });
    };

    const handleSave = async () => {
        if (!editing) return;
        if (!editing.name?.trim()) {
            toast.error("Clause name is required.");
            return;
        }
        if ((editing.body_html || "").length > MAX_BODY_CHARS) {
            toast.error(`Body is too long (${editing.body_html.length} / ${MAX_BODY_CHARS} chars).`);
            return;
        }
        setBusy(true);
        try {
            if (editing.id) {
                const r = await api.patch(`/admin/clauses/${editing.id}`, {
                    name: editing.name,
                    description: editing.description,
                    category: editing.category,
                    body_html: editing.body_html,
                });
                toast.success(`Updated "${r.data.name}"`);
            } else {
                const r = await api.post("/admin/clauses", {
                    name: editing.name,
                    description: editing.description,
                    category: editing.category,
                    body_html: editing.body_html,
                });
                toast.success(`Created "${r.data.name}"`);
            }
            setEditing(null);
            await refresh();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    const handleArchiveToggle = async (c) => {
        // Task 10/10b — soft archive (reversible). Hard-delete is in
        // ``handleHardDelete`` below.
        try {
            if (c.archived) {
                await api.post(`/admin/clauses/${c.id}/unarchive`);
                toast.success(`Restored "${c.name}"`);
                await refresh();
                return;
            }
            const { data } = await api.post(
                `/admin/clauses/${c.id}/archive`,
                { confirm: false },
            );
            const refCount = Object.values(data?.references || {}).reduce((a, b) => a + b, 0);
            toast.success(refCount > 0
                ? `Archived "${c.name}" (was referenced by ${refCount} record${refCount === 1 ? "" : "s"})`
                : `Archived "${c.name}"`);
            await refresh();
        } catch (err) {
            const status = err?.response?.status;
            const detail = err?.response?.data?.detail;
            if (status === 409 && detail?.references) {
                const lines = Object.entries(detail.references)
                    .map(([k, v]) => `  • ${v} ${k.replace(/_/g, " ")}`).join("\n");
                const ok = window.confirm(
                    `This clause is referenced by:\n${lines}\n\nArchive anyway?`
                );
                if (!ok) return;
                try {
                    await api.post(`/admin/clauses/${c.id}/archive`, { confirm: true });
                    toast.success(`Archived "${c.name}"`);
                    await refresh();
                } catch (e2) { toast.error(formatApiError(e2)); }
            } else {
                toast.error(formatApiError(err));
            }
        }
    };

    // Task 10c — additional hard-delete pathway alongside archive.
    // Clauses have no live FK linkage today, so the cascade is empty
    // and the row is removed directly.
    const handleHardDelete = async (c) => {
        const ok = window.confirm(
            `Permanently delete clause "${c.name}"?\n\n` +
            "Templates that already embedded this clause keep their copy " +
            "(clauses are copy-pasted at insertion time). " +
            "This action cannot be undone.\n\nContinue?"
        );
        if (!ok) return;
        try {
            await api.post(`/admin/clauses/${c.id}/delete`, { confirm: true });
            toast.success(`Deleted "${c.name}".`);
            await refresh();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    // Role gate. Show the page but block CRUD UI for non-super-admin.
    if (!user) return null;
    if (user.role !== "super_admin" && user.role !== "admin") {
        return (
            <_EmptyShell>
                <p className="text-sm text-slate-600">
                    You don't have access to the Clause Library.{" "}
                    <Link to="/admin" className="text-blue-700 underline">Go back</Link>.
                </p>
            </_EmptyShell>
        );
    }

    return (
        <DashboardShell>
            <div className="max-w-6xl mx-auto p-6 space-y-4" data-testid="clauses-page">
                <header className="flex items-start justify-between gap-3">
                    <div>
                        <h1 className="text-2xl font-semibold text-slate-900 inline-flex items-center gap-2">
                            <BookOpen size={20} /> Clause Library
                        </h1>
                        <p className="text-sm text-slate-500 mt-1">
                            Reusable text snippets you can drop into any template. Curated by Super Admins.
                        </p>
                    </div>
                    {isSuperAdmin && (
                        <Button
                            size="sm"
                            data-testid="clauses-new-button"
                            onClick={startNew}
                            className="bg-blue-900 hover:bg-blue-800">
                            <Plus size={14} className="mr-1" /> New clause
                        </Button>
                    )}
                </header>

                <Card>
                    <CardContent className="p-4 flex flex-wrap items-center gap-3">
                        <div className="relative flex-1 min-w-[220px]">
                            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                            <Input
                                placeholder="Search name, category, or description…"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                data-testid="clauses-search-input"
                                className="pl-7 h-9 text-sm"
                            />
                        </div>
                        <label className="inline-flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                            <Switch
                                checked={showArchived}
                                onCheckedChange={setShowArchived}
                                data-testid="clauses-archived-toggle"
                            />
                            Show archived
                        </label>
                    </CardContent>
                </Card>

                {/* New / edit form (inline, top of page). */}
                {editing && (
                    <Card data-testid="clauses-editor-card">
                        <CardContent className="p-5 space-y-3">
                            <div className="flex items-center justify-between gap-3">
                                <h2 className="text-base font-semibold text-slate-900">
                                    {editing.id ? "Edit clause" : "New clause"}
                                </h2>
                                <Button
                                    variant="ghost" size="sm"
                                    data-testid="clauses-editor-cancel"
                                    onClick={() => setEditing(null)}
                                    disabled={busy}>
                                    <X size={14} className="mr-1" /> Cancel
                                </Button>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                <div className="space-y-1.5">
                                    <Label htmlFor="cl_name">Name *</Label>
                                    <Input
                                        id="cl_name"
                                        data-testid="clauses-editor-name"
                                        value={editing.name}
                                        onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                                        placeholder="e.g. Standard force majeure"
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <Label htmlFor="cl_cat">Category</Label>
                                    <Input
                                        id="cl_cat"
                                        data-testid="clauses-editor-category"
                                        value={editing.category}
                                        onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                                        placeholder="e.g. Boilerplate, Footnote, Indemnity"
                                        list="clause-cat-suggest"
                                        maxLength={80}
                                    />
                                    <datalist id="clause-cat-suggest">
                                        {categories.map((c) => <option key={c} value={c} />)}
                                    </datalist>
                                </div>
                                <div className="space-y-1.5">
                                    <Label htmlFor="cl_desc">Description</Label>
                                    <Input
                                        id="cl_desc"
                                        data-testid="clauses-editor-description"
                                        value={editing.description || ""}
                                        onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                                        placeholder="Short summary for your team"
                                    />
                                </div>
                            </div>
                            <div className="space-y-1.5">
                                <Label>Body</Label>
                                <MiniEditor
                                    mode="header"
                                    initialHtml={editing.body_html || ""}
                                    onChange={(html) => setEditing((e) => ({ ...e, body_html: html }))}
                                />
                            </div>
                            <div className="flex items-center justify-end gap-2 pt-2">
                                <Button
                                    type="button"
                                    size="sm"
                                    data-testid="clauses-editor-save"
                                    onClick={handleSave}
                                    disabled={busy}
                                    className="bg-blue-900 hover:bg-blue-800">
                                    {busy ? <Loader2 size={12} className="mr-1 animate-spin" /> : <Save size={12} className="mr-1" />}
                                    {editing.id ? "Save changes" : "Create clause"}
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Grouped list */}
                <Card>
                    <CardContent className="p-0">
                        {loading && (
                            <div className="p-6 text-sm text-slate-500 inline-flex items-center gap-2">
                                <Loader2 size={14} className="animate-spin" /> Loading clauses…
                            </div>
                        )}
                        {!loading && filtered.length === 0 && (
                            <div className="p-8 text-sm text-slate-500" data-testid="clauses-empty">
                                {search.trim()
                                    ? `No clauses match "${search}".`
                                    : showArchived
                                        ? "No archived clauses."
                                        : "No clauses yet — click \"New clause\" to add the first one."}
                            </div>
                        )}
                        {grouped.map(([cat, arr]) => (
                            <div key={cat} data-testid={`clauses-group-${cat}`}>
                                <div className="px-4 py-2 text-[10px] uppercase tracking-wider font-medium text-slate-500 bg-slate-50 border-b border-slate-200">
                                    {cat} · {arr.length} {arr.length === 1 ? "clause" : "clauses"}
                                </div>
                                <ul>
                                    {arr.map((c) => (
                                        <li key={c.id}
                                            data-testid={`clauses-row-${c.id}`}
                                            className="px-4 py-3 border-b border-slate-100 last:border-b-0 flex items-start justify-between gap-3 hover:bg-slate-50/60">
                                            <div className="min-w-0 flex-1">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-sm font-medium text-slate-900 truncate">{c.name}</span>
                                                    {c.archived && (
                                                        <span className="text-[10px] uppercase tracking-wider text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">
                                                            Archived
                                                        </span>
                                                    )}
                                                </div>
                                                {c.description && (
                                                    <p className="text-[12px] text-slate-500 truncate mt-0.5">{c.description}</p>
                                                )}
                                                <div className="text-[10px] text-slate-400 mt-1">
                                                    {(c.body_html || "").length} chars · last updated{" "}
                                                    {new Date(c.updated_at).toLocaleDateString()}
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-1 shrink-0">
                                                {isSuperAdmin && (
                                                    <>
                                                        <Button
                                                            variant="ghost" size="sm"
                                                            data-testid={`clauses-edit-${c.id}`}
                                                            onClick={() => setEditing({ ...c })}>
                                                            Edit
                                                        </Button>
                                                        <Button
                                                            variant="ghost" size="sm"
                                                            data-testid={`clauses-archive-${c.id}`}
                                                            onClick={() => handleArchiveToggle(c)}
                                                            className={c.archived
                                                                ? "text-emerald-700 hover:bg-emerald-50"
                                                                : "text-amber-700 hover:bg-amber-50"}>
                                                            {c.archived
                                                                ? <><ArchiveRestore size={13} className="mr-1" /> Restore</>
                                                                : <><Archive size={13} className="mr-1" /> Archive</>}
                                                        </Button>
                                                        {/* Task 10c — hard-delete alongside archive. */}
                                                        <Button
                                                            variant="ghost" size="sm"
                                                            data-testid={`clauses-delete-${c.id}`}
                                                            onClick={() => handleHardDelete(c)}
                                                            className="text-red-700 hover:bg-red-50">
                                                            <Trash2 size={13} className="mr-1" /> Delete
                                                        </Button>
                                                    </>
                                                )}
                                                {!isSuperAdmin && (
                                                    <span className="text-[11px] text-slate-400 italic">
                                                        Super Admin only
                                                    </span>
                                                )}
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </CardContent>
                </Card>
            </div>
        </DashboardShell>
    );
}
